import { call, put, select, take } from "redux-saga/effects";
import CookieUtil from "../../../utils/storage/cookieUtil";
import { SELECTED_SAVED_ADDRESS } from "../../../constants/storage";
import { setFusData } from "../../slices/common/fus";
import { requestToSavedAddress, savedAddressError, setSavedAddress } from "../../slices/savedAddresses";

/**
 * Waits for FUS to finish when login state is not yet available.
 */
export function* waitForFusCompletion() {
  const isLoggedIn = yield select((state) => state?.fus?.loginDetail?.loggedin);
  if (isLoggedIn === undefined) {
    yield take(setFusData.type);
  }
}

/**
 * For logged-in users with a selected saved address, waits until saved addresses are loaded.
 */
export function* waitForSavedAddressesWhenLoggedIn() {
  const isLoggedIn = yield select((state) => state?.fus?.loginDetail?.loggedin);
  if (!isLoggedIn) {
    return;
  }

  const selectedSavedAddressId = CookieUtil.getCookie(SELECTED_SAVED_ADDRESS);
  if (!selectedSavedAddressId) {
    return;
  }

  const savedAddresses = yield select((state) => state?.savedAddress?.savedAddresses);
  if (Array.isArray(savedAddresses) && savedAddresses.length > 0) {
    return;
  }

  const hasFetched = yield select((state) => state?.savedAddress?.hasSavedAddressesFetched);
  if (hasFetched) {
    return;
  }

  const isLoading = yield select((state) => state?.savedAddress?.isSavedAddressesLoading);
  if (!isLoading) {
    yield put(requestToSavedAddress());
  }

  yield take([setSavedAddress.type, savedAddressError.type]);
}

/**
 * Resolves lat/long from the selected saved address cookie, if available.
 *
 * @param {Array<Object>} [savedAddresses]
 * @returns {{ lat?: number, long?: number }}
 */
export function getLatLongFromSavedAddressSync(savedAddresses) {
  const selectedSavedAddressId = CookieUtil.getCookie(SELECTED_SAVED_ADDRESS);
  if (!selectedSavedAddressId || !Array.isArray(savedAddresses)) {
    return {};
  }

  const selectedSavedAddress = savedAddresses.find((address) => address?.postalAddress === selectedSavedAddressId);

  if (selectedSavedAddress?.latitude && selectedSavedAddress?.longitude) {
    return {
      lat: selectedSavedAddress.latitude,
      long: selectedSavedAddress.longitude,
    };
  }

  return {};
}

/**
 * Resolves lat/long from the selected saved address cookie, if available.
 *
 * @returns {{ lat?: number, long?: number }}
 */
export function* getLatLongFromSavedAddress() {
  const savedAddresses = yield select((state) => state?.savedAddress?.savedAddresses);
  return getLatLongFromSavedAddressSync(savedAddresses);
}

/**
 * Waits for prerequisites and resolves lat/long for serviceability APIs.
 *
 * @returns {{ lat?: number, long?: number }}
 */
export function* resolveLatLongForServiceability() {
  yield call(waitForFusCompletion);
  yield call(waitForSavedAddressesWhenLoggedIn);
  return yield call(getLatLongFromSavedAddress);
}
